import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Avatar,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Vaccines,
  BugReport,
  MedicalServices,
  Restaurant,
  ShoppingCart,
  Schedule,
  CheckCircle,
  Warning,
  Info,
  MoreVert
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ro } from 'date-fns/locale';
import { Recommendation } from '@services/recommendationsService';

interface RecommendationCardProps {
  recommendation: Recommendation & { animalName?: string; dueAt?: string; resolvedAt?: string };
  onMarkAsResolved: () => void;
  onCreateAppointment: () => void;
  onAddToCart: () => void;
}

const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  onMarkAsResolved,
  onCreateAppointment,
  onAddToCart
}) => {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'vaccin': return <Vaccines />;
      case 'deparazitare': return <BugReport />;
      case 'control': return <MedicalServices />;
      case 'nutritie': return <Restaurant />;
      case 'produs': return <ShoppingCart />;
      default: return <MedicalServices />;
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'urgent': return <Warning sx={{ color: 'error.main' }} />;
      case 'important': return <Info sx={{ color: 'warning.main' }} />;
      default: return <CheckCircle sx={{ color: 'info.main' }} />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'urgent': return 'error';
      case 'important': return 'warning';
      default: return 'info';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'vaccin': return 'success';
      case 'deparazitare': return 'warning';
      case 'control': return 'info';
      case 'nutritie': return 'secondary';
      case 'produs': return 'primary';
      default: return 'default';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'vaccin': return 'Vaccin';
      case 'deparazitare': return 'Deparazitare';
      case 'control': return 'Control';
      case 'nutritie': return 'Nutriție';
      case 'produs': return 'Produs';
      default: return 'Recomandare';
    }
  };

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case 'urgent': return 'Urgent';
      case 'important': return 'Important';
      default: return 'Informativ';
    }
  };

  const isOverdue = (recommendation.dueAt || recommendation.due_at) && new Date(recommendation.dueAt || recommendation.due_at!) < new Date();
  const isResolved = !!(recommendation.resolvedAt || recommendation.resolved_at);

  return (
    <Card 
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4
        },
        border: isOverdue ? '2px solid' : '1px solid',
        borderColor: isOverdue ? 'error.main' : 'divider'
      }}
    >
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header cu tip și severitate */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              sx={{
                bgcolor: `${getTypeColor(recommendation.type)}.light`,
                color: `${getTypeColor(recommendation.type)}.main`,
                width: 40,
                height: 40
              }}
            >
              {getTypeIcon(recommendation.type)}
            </Avatar>
            <Box>
              <Chip
                label={getTypeLabel(recommendation.type)}
                size="small"
                color={getTypeColor(recommendation.type) as any}
                sx={{ mb: 0.5 }}
              />
              <Typography variant="caption" color="text.secondary" display="block">
                {recommendation.animalName || 'Animal'}
              </Typography>
            </Box>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Chip
              icon={getSeverityIcon(recommendation.severity)}
              label={getSeverityLabel(recommendation.severity)}
              size="small"
              color={getSeverityColor(recommendation.severity) as any}
              variant={recommendation.severity === 'urgent' ? 'filled' : 'outlined'}
            />
            {isResolved && (
              <Chip
                icon={<CheckCircle />}
                label="Rezolvat"
                size="small"
                color="success"
                variant="filled"
              />
            )}
          </Box>
        </Box>

        {/* Titlu și descriere */}
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, flex: 1 }}>
          {recommendation.title}
        </Typography>
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            mb: 2, 
            flex: 1,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {recommendation.description}
        </Typography>

        {/* Data scadentă */}
        {(recommendation.dueAt || recommendation.due_at) && (
          <Box mb={2}>
            <Typography variant="caption" color="text.secondary">
              Scadentă: {formatDistanceToNow(new Date(recommendation.dueAt || recommendation.due_at!), { 
                addSuffix: true, 
                locale: ro 
              })}
            </Typography>
            {isOverdue && (
              <Chip
                label="Întârziere"
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Acțiuni */}
        {!isResolved ? (
          <Box display="flex" gap={1} flexWrap="wrap">
            {recommendation.type === 'vaccin' || recommendation.type === 'control' ? (
              <Button
                variant="contained"
                size="small"
                startIcon={<Schedule />}
                onClick={onCreateAppointment}
                sx={{ flex: 1, minWidth: 'fit-content' }}
              >
                Programează
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                startIcon={<ShoppingCart />}
                onClick={onAddToCart}
                sx={{ flex: 1, minWidth: 'fit-content' }}
              >
                Adaugă în coș
              </Button>
            )}
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<CheckCircle />}
              onClick={onMarkAsResolved}
              sx={{ flex: 1, minWidth: 'fit-content' }}
            >
              Marchează ca făcut
            </Button>
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color="success" fontSize="small" />
            <Typography variant="caption" color="success.main">
              Rezolvat pe {new Date(recommendation.resolvedAt || recommendation.resolved_at!).toLocaleDateString('ro-RO')}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default RecommendationCard;
